import { useEffect, useState, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ServiceWorkerUpdateProvider = () => {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  const reloadPage = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
    }
    window.location.reload();
  }, [waitingWorker]);

  useEffect(() => {
    // Only run if service workers are supported
    if (!("serviceWorker" in navigator)) return;

    const handleControllerChange = () => {
      window.location.reload();
    };

    const showUpdateToast = (worker: ServiceWorker) => {
      setWaitingWorker(worker);
      
      toast({
        title: "🎉 Update Available",
        description: "A new version of CheckFirst is ready. Refresh to enjoy the latest improvements!",
        duration: 0, // Keep visible until dismissed
      });
    };

    const checkForUpdates = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        
        if (!registration) return;

        // Check if there's already a waiting worker
        if (registration.waiting) {
          showUpdateToast(registration.waiting);
        }

        // Listen for new service worker installing
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              showUpdateToast(newWorker);
            }
          });
        });

        // Listen for controller changes
        navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
      } catch (error) {
        console.error("Error checking for SW updates:", error);
      }
    };

    checkForUpdates();

    // Periodically check for updates (every 60 seconds)
    const intervalId = setInterval(() => {
      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.update();
      });
    }, 60 * 1000);

    return () => {
      clearInterval(intervalId);
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, []);

  // Render a floating update button if an update is available
  if (!waitingWorker) return null;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300">
      <Button
        onClick={reloadPage}
        className="gap-2 shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-full"
        size="lg"
      >
        <RefreshCw className="w-4 h-4" />
        Update Available – Refresh
      </Button>
    </div>
  );
};
